class LaunchFragranceShowcase {
  constructor(root) {
    this.root = root;
    if (root.dataset.launchInitialized === 'true') return;
    root.dataset.launchInitialized = 'true';

    this.products = this.getProducts();
    this.activeIndex = 0;
    this.storyIndex = 0;
    this.lightboxOpen = false;
    this.isDraggingStory = false;
    this.storyDragMoved = false;
    this.storyDragStartX = 0;
    this.storyDragStartScrollLeft = 0;
    this.storyPointerSlide = null;
    this.storySuppressClick = false;
    this.boundStoryMouseMove = null;
    this.boundStoryMouseUp = null;

    this.refs = {
      image: root.querySelector('[data-product-image]'),
      title: root.querySelector('[data-product-title]'),
      subtitle: root.querySelector('[data-product-subtitle]'),
      price: root.querySelector('[data-product-price]'),
      comparePrice: root.querySelector('[data-product-compare-price]'),
      cta: root.querySelector('[data-product-cta]'),
      variantInput: root.querySelector('[data-product-variant-input]'),
      support: root.querySelector('[data-support]'),
      videoStage: root.querySelector('.launch-fragrance-showcase__video-stage'),
      variantWrap: root.querySelector('[data-variant-wrap]'),
      variantRow: root.querySelector('[data-variant-row]'),
      story: root.querySelector('[data-story-carousel]'),
      storyCount: root.querySelector('[data-story-count]'),
      storyTrack: root.querySelector('[data-story-track]'),
      storyDots: root.querySelector('[data-story-dots]'),
      storyLightbox: root.querySelector('[data-story-lightbox]'),
      storyLightboxImage: root.querySelector('[data-story-lightbox-image]')
    };

    this.bind();
    this.bindStoryDrag();
    this.renderProduct(0);
  }

  getProducts() {
    const node = this.root.querySelector('[data-products-json]');
    if (!node) return [];

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return [];
    }
  }

  bind() {
    this.root.addEventListener('click', (event) => {
      const productTrigger = event.target.closest('[data-product-trigger]');
      if (productTrigger) {
        const index = this.products.findIndex((item) => item.blockId === productTrigger.dataset.blockId);
        if (index >= 0) this.renderProduct(index);
        return;
      }

      const variantTrigger = event.target.closest('[data-variant-trigger]');
      if (variantTrigger) {
        this.renderVariant(Number(variantTrigger.dataset.variantId));
        return;
      }

      const storyTrigger = event.target.closest('[data-story-slide]');
      if (storyTrigger) {
        if (this.storySuppressClick) {
          this.storySuppressClick = false;
          return;
        }
        if (this.storyDragMoved) {
          this.storyDragMoved = false;
          return;
        }
        this.selectStory(Number(storyTrigger.dataset.storyIndex));
        return;
      }

      if (event.target.closest('[data-story-lightbox-close]')) {
        this.closeStoryLightbox();
        return;
      }

      if (event.target.closest('[data-story-prev]')) {
        this.renderStory(this.storyIndex - 1);
        return;
      }

      if (event.target.closest('[data-story-next]')) {
        this.renderStory(this.storyIndex + 1);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.lightboxOpen) {
        this.closeStoryLightbox();
      }
    });
  }

  bindStoryDrag() {
    const track = this.refs.storyTrack;
    if (!track) return;
    const hasPointerEvents = typeof window !== 'undefined' && 'PointerEvent' in window;

    const startDrag = (clientX, pointerId = null) => {
      this.isDraggingStory = true;
      this.storyDragMoved = false;
      this.storyDragStartX = clientX;
      this.storyDragStartScrollLeft = track.scrollLeft;
      track.classList.add('is-dragging');
      if (pointerId != null) {
        track.setPointerCapture?.(pointerId);
      }
    };

    const activateStoryFromPress = () => {
      if (!this.storyPointerSlide || this.storyDragMoved) return;

      const index = Number(this.storyPointerSlide.dataset.storyIndex);
      if (Number.isNaN(index)) return;

      this.storySuppressClick = true;
      this.selectStory(index);
      window.setTimeout(() => {
        this.storySuppressClick = false;
      }, 0);
    };

    const moveDrag = (clientX) => {
      if (!this.isDraggingStory) return;

      const deltaX = clientX - this.storyDragStartX;
      if (Math.abs(deltaX) > 6) {
        this.storyDragMoved = true;
      }

      track.scrollLeft = this.storyDragStartScrollLeft - deltaX;
    };

    const endDrag = (event = null) => {
      if (!this.isDraggingStory) return;
      activateStoryFromPress();
      this.isDraggingStory = false;
      this.storyPointerSlide = null;
      track.classList.remove('is-dragging');
      if (event?.pointerId != null) {
        try {
          track.releasePointerCapture?.(event.pointerId);
        } catch (error) {
          // Ignore capture release errors from non-captured pointers.
        }
      }

      window.setTimeout(() => {
        this.storyDragMoved = false;
      }, 0);
    };

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.storyPointerSlide = event.target.closest('[data-story-slide]');
      startDrag(event.clientX, event.pointerId);
    });

    track.addEventListener('pointermove', (event) => {
      if (!this.isDraggingStory) return;
      event.preventDefault();
      moveDrag(event.clientX);
    });

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('lostpointercapture', endDrag);

    this.boundStoryMouseMove = (event) => {
      if (!this.isDraggingStory) return;
      event.preventDefault();
      moveDrag(event.clientX);
    };

    this.boundStoryMouseUp = () => {
      endDrag();
      document.removeEventListener('mousemove', this.boundStoryMouseMove);
      document.removeEventListener('mouseup', this.boundStoryMouseUp);
    };

    if (!hasPointerEvents) {
      track.addEventListener('mousedown', (event) => {
        if (event.button !== 0) return;
        this.storyPointerSlide = event.target.closest('[data-story-slide]');
        startDrag(event.clientX);
        document.addEventListener('mousemove', this.boundStoryMouseMove);
        document.addEventListener('mouseup', this.boundStoryMouseUp);
      });
    }
  }

  renderProduct(index) {
    const item = this.products[index];
    if (!item || !this.refs.title || !this.refs.subtitle) return;

    this.activeIndex = index;
    this.storyIndex = 0;
    this.closeStoryLightbox();

    this.root.querySelectorAll('[data-product-trigger]').forEach((button) => {
      button.classList.toggle('is-active', button.dataset.blockId === item.blockId);
    });

    this.refs.title.textContent = item.product.title;
    this.refs.subtitle.textContent = item.subtitle || '';
    this.renderSupport(item);
    this.renderVariantButtons(item);
    this.renderVariant(Number(item.product.variantId));
    this.renderStories(item);
  }

  renderSupport(item) {
    if (!this.refs.support || !this.refs.videoStage) return;

    const hasSupport = Boolean(item.supportImage || item.supportText);
    this.refs.support.hidden = !hasSupport;
    this.refs.videoStage.classList.toggle('launch-fragrance-showcase__video-stage--no-support', !hasSupport);

    if (!hasSupport) {
      this.refs.support.innerHTML = '';
      return;
    }

    const parts = [];
    if (item.supportImage && item.supportMode !== 'text') {
      parts.push(`<img class="launch-fragrance-showcase__support-image" src="${item.supportImage}" alt="${item.product.title}">`);
    }
    if (item.supportText && item.supportMode !== 'image') {
      parts.push(`<div class="launch-fragrance-showcase__support-text">${item.supportText}</div>`);
    }

    this.refs.support.innerHTML = parts.join('');
  }

  renderVariantButtons(item) {
    if (!this.refs.variantWrap || !this.refs.variantRow) return;

    const variants = item.product.variants || [];
    this.refs.variantWrap.hidden = variants.length <= 1;
    this.refs.variantRow.innerHTML = variants.map((variant) => `
      <button type="button" class="launch-fragrance-showcase__variant" data-variant-trigger data-variant-id="${variant.id}" ${variant.available ? '' : 'disabled'}>
        ${variant.title}
      </button>
    `).join('');
  }

  renderVariant(variantId) {
    const item = this.products[this.activeIndex];
    if (!item || !this.refs.image || !this.refs.price || !this.refs.comparePrice) return;

    const variant = item.product.variants.find((entry) => entry.id === variantId) || item.product.variants[0];
    if (!variant) return;

    this.root.querySelectorAll('[data-variant-trigger]').forEach((button) => {
      button.classList.toggle('is-active', Number(button.dataset.variantId) === variant.id);
    });

    this.refs.image.src = variant.image || item.product.featuredImage;
    this.refs.image.alt = item.product.featuredAlt || item.product.title;
    this.refs.price.textContent = variant.price || '';
    this.refs.comparePrice.textContent = variant.compareAtPrice || '';
    this.refs.comparePrice.hidden = !(variant.compareAtPrice && variant.compareAtPrice !== variant.price);

    if (this.refs.variantInput) {
      this.refs.variantInput.value = variant.id;
    }

    if (this.refs.cta) {
      this.refs.cta.disabled = !variant.available;
      this.refs.cta.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
    }
  }

  renderStories(item) {
    const stories = item.stories || [];
    if (!this.refs.story || !this.refs.storyTrack || !this.refs.storyDots || !this.refs.storyCount) return;

    if (!stories.length) {
      this.refs.story.hidden = true;
      this.refs.storyTrack.innerHTML = '';
      this.refs.storyDots.innerHTML = '';
      this.closeStoryLightbox();
      return;
    }

    this.refs.story.hidden = false;
    this.refs.storyTrack.innerHTML = stories.map((story, index) => `
      <button type="button" class="launch-fragrance-showcase__story-card${index === 0 ? ' is-active' : ''}" data-story-slide data-story-index="${index}" aria-label="Show story image ${index + 1}">
        <img class="launch-fragrance-showcase__story-image" src="${story.image}" alt="${story.title || item.product.title}">
      </button>
    `).join('');
    this.refs.storyDots.innerHTML = stories.map((_, index) => `<span class="launch-fragrance-showcase__story-dot${index === 0 ? ' is-active' : ''}"></span>`).join('');

    this.renderStory(0);
  }

  selectStory(index) {
    this.renderStory(index);
    this.openStoryLightbox();
  }

  renderStory(index) {
    const item = this.products[this.activeIndex];
    if (!item || !item.stories || !item.stories.length) return;

    const max = item.stories.length;
    this.storyIndex = (index + max) % max;
    this.refs.storyCount.textContent = `${String(this.storyIndex + 1).padStart(2, '0')} / ${String(max).padStart(2, '0')}`;

    this.refs.storyTrack.querySelectorAll('[data-story-slide]').forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === this.storyIndex);
    });

    this.refs.storyDots.querySelectorAll('.launch-fragrance-showcase__story-dot').forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === this.storyIndex);
    });

    this.scrollActiveStoryIntoView();

    if (this.lightboxOpen) {
      this.syncStoryLightbox();
    }
  }

  scrollActiveStoryIntoView() {
    if (!this.refs.storyTrack) return;

    const activeSlide = this.refs.storyTrack.querySelector('.launch-fragrance-showcase__story-card.is-active');
    const track = this.refs.storyTrack;
    if (!activeSlide || typeof track.scrollTo !== 'function') return;

    const targetLeft = activeSlide.offsetLeft - ((track.clientWidth - activeSlide.clientWidth) / 2);
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const nextScrollLeft = Math.min(Math.max(0, targetLeft), maxScrollLeft);

    track.scrollTo({
      left: nextScrollLeft,
      behavior: 'smooth'
    });
  }

  openStoryLightbox() {
    if (!this.refs.storyLightbox || !this.refs.storyLightboxImage) return;
    this.syncStoryLightbox();
    this.refs.storyLightbox.hidden = false;
    this.lightboxOpen = true;
  }

  syncStoryLightbox() {
    const item = this.products[this.activeIndex];
    if (!item || !item.stories || !item.stories.length || !this.refs.storyLightboxImage) return;

    const story = item.stories[this.storyIndex];
    if (!story) return;

    this.refs.storyLightboxImage.src = story.image;
    this.refs.storyLightboxImage.alt = story.title || item.product.title;
  }

  closeStoryLightbox() {
    if (!this.refs.storyLightbox || !this.refs.storyLightboxImage) return;
    this.refs.storyLightbox.hidden = true;
    this.refs.storyLightboxImage.src = '';
    this.refs.storyLightboxImage.alt = '';
    this.lightboxOpen = false;
  }
}

function initLaunchFragranceShowcases(scope = document) {
  Array.prototype.forEach.call(scope.querySelectorAll('.launch-fragrance-showcase'), function(root) {
    new LaunchFragranceShowcase(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initLaunchFragranceShowcases());
} else {
  initLaunchFragranceShowcases();
}

document.addEventListener('shopify:section:load', (event) => {
  initLaunchFragranceShowcases(event.target);
});
